'use client';

import { useState, useMemo, useEffect, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import { flexRender, useTable } from '@tanstack/react-table';
import type { ColumnDef } from '@/lib/table-features';
import {
  ArrowLeft,
  Camera,
  FileText,
  Loader2,
  Trash2,
  Package,
  ShieldCheck,
  Plus,
  Copy,
  QrCode,
  ScanLine,
  PackageCheck,
  Search
} from 'lucide-react';
import { QrScanDialog } from '@/components/qr-scan-dialog';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';

import { PrintPortal } from '@/components/print-portal';
import { useRouter, Link } from '@/i18n/routing';
import { useHashDialog, useHashParamDialog } from '@/hooks/use-hash-dialog';
import { cn } from '@/lib/utils';
import { IS_QR_SCANNER_ENABLED } from '@/lib/feature-flags';
import { appTableFeatures } from '@/lib/table-features';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { DataTableViewOptions } from '@/components/ui/table/data-table-view-options';
import {
  useAddRegistrationToBoxMutation,
  useRemoveRegistrationFromBoxMutation,
  useUpdateBoxMutation,
  useOpenBoxMutation,
  useCloseBoxMutation
} from '../services/packing.mutations';
import { isApiError } from '@/lib/api/client';
import { packingQueries } from '../services/packing.queries';
import { PACKING_HASH } from '../constants';
import type { Box, BoxStatus } from '../types';
import { InfiniteCombobox } from '@/components/forms/infinite-combobox';
import { useInfiniteSelect } from '@/hooks/use-infinite-select';
import {
  registrationQueries,
  StagePill,
  SubStatusCell,
  type Registration
} from '@/features/registration';
import {
  dynamicFormQueries,
  dynamicFormKeys,
  dynamicFormsApi,
  type DynamicForm,
  type DynamicFormField
} from '@/features/dynamic-forms';
import { useHardwareScanner } from '@/hooks/use-hardware-scanner';
import { useFilteredColumns } from '@/hooks/use-filtered-columns';
import { usePermissions } from '@/hooks/use-permissions';
import { buildDynamicFormColumns } from '@/lib/build-dynamic-form-columns';
import { DynamicFormResponsesDisplay } from '@/components/dynamic-form-responses-display';

function isClientMismatchError(err: unknown): boolean {
  if (!isApiError(err) || err.status !== 422) return false;
  const code = (err.data as { errors?: { code?: string } } | undefined)?.errors
    ?.code;
  return code === 'client_mismatch';
}

const STATUS_STYLES: Record<BoxStatus, string> = {
  available:
    'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  packing: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  ready_for_shipping:
    'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  in_shipping:
    'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  in_transit: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  received: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200'
};

interface BoxDetailPageProps {
  boxId: number;
}

export function BoxDetailPage({ boxId }: BoxDetailPageProps) {
  const t = useTranslations('packing');
  const tc = useTranslations('common');
  const tReg = useTranslations('registration');
  const router = useRouter();
  // Overlay open-state lives in the URL hash (#add, #scan, #remove/<id>,
  // #client-mismatch) so overlays are deep-linkable, survive refresh, and close
  // on browser Back. Destructure into stable locals so they're legal in the
  // React Compiler dep arrays (never reference the hook object property there).
  const { open: addOpen, onOpenChange: toggleAdd } = useHashDialog(
    PACKING_HASH.add
  );
  const { open: scanOpen, onOpenChange: toggleScan } = useHashDialog(
    PACKING_HASH.scan
  );
  const {
    open: removeOpen,
    param: removeId,
    openWith: openRemove,
    onOpenChange: closeRemove
  } = useHashParamDialog(PACKING_HASH.remove);
  const { open: mismatchOpen, onOpenChange: toggleMismatch } = useHashDialog(
    PACKING_HASH.clientMismatch
  );
  const [scannerActive, setScannerActive] = useState(true);
  const [selectedFormIdStr, setSelectedFormIdStr] = useState('');

  // ─── Dynamic form selector ──────────────────────────────────────────
  const { data: activeTemplate } = useQuery(
    dynamicFormQueries.activeByType('registration')
  );
  // Searchable, server-paginated list of selectable registration forms so the
  // option list is never capped by a fixed `per_page`.
  const formsSelect = useInfiniteSelect<DynamicForm>({
    queryKey: dynamicFormKeys.lists(),
    queryFn: (params) => dynamicFormsApi.getAll(params),
    mapOption: useCallback(
      (f: DynamicForm) => ({
        value: String(f.id),
        label: f.is_default
          ? `${f.name} (${t('defaultForm') ?? 'Default'})`
          : f.name
      }),
      [t]
    ),
    params: { type: 'registration', status: 'active' }
  });
  const activeFormId = activeTemplate?.data?.id;
  const selectedFormId = selectedFormIdStr
    ? Number(selectedFormIdStr)
    : activeFormId;

  const { data: selectedFormData } = useQuery({
    ...dynamicFormQueries.detail(selectedFormId ?? 0),
    enabled: !!selectedFormId && selectedFormId !== activeFormId
  });
  const selectedForm =
    selectedFormId === activeFormId
      ? activeTemplate?.data
      : selectedFormData?.data;
  const dynamicFormFields: DynamicFormField[] = useMemo(
    () => selectedForm?.fields ?? [],
    [selectedForm?.fields]
  );

  const addRegMutation = useAddRegistrationToBoxMutation();
  const removeRegMutation = useRemoveRegistrationFromBoxMutation();
  const updateBoxMutation = useUpdateBoxMutation();
  const openBoxMutation = useOpenBoxMutation();
  const closeBoxMutation = useCloseBoxMutation();

  // Client-mismatch is a hybrid overlay: the hash (#client-mismatch) drives its
  // open-state, but the transient container-number payload stays in local state
  // — it's error-driven (a 422 API response), not a user click, and carries a
  // container NUMBER, so it isn't deep-linkable to a resolvable row id.
  const [mismatchContainer, setMismatchContainer] = useState<string | null>(
    null
  );

  const { hasPermission } = usePermissions();
  // The screen is readable by `boxes.view` and by `supervisor.view`, but every
  // action on it posts to a `boxes.edit` route. Status alone decided which
  // buttons showed, so a read-only account got a toolbar of 403s.
  const canEditBox = hasPermission('boxes.edit');
  const { data: boxData, isLoading } = useQuery(packingQueries.detail(boxId));
  const box = boxData?.data as Box | undefined;

  // Opening the box for filling is a write. A reader landing on an `available`
  // box used to fire it on mount and collect a 403 before touching anything.
  useEffect(() => {
    if (box?.status === 'available' && canEditBox) {
      openBoxMutation.mutate(boxId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [box?.status, canEditBox]);

  const [seal1, setSeal1] = useState('');
  const [seal2, setSeal2] = useState('');
  const [sealsInitialized, setSealsInitialized] = useState(false);

  // Sync seal state when box data loads
  if (box && !sealsInitialized) {
    setSeal1(box.security_seal_1 ?? '');
    setSeal2(box.security_seal_2 ?? '');
    setSealsInitialized(true);
  }

  const [searchQuery, setSearchQuery] = useState('');

  // Box destination is the client of the first container already packed. Until
  // the first container is added, accept any client (and the backend's
  // pack-by-destination guard short-circuits when the box is empty).
  const expectedClientId = box?.registrations?.[0]?.client_id ?? null;

  const { data: searchData, isFetching: isSearching } = useQuery({
    ...registrationQueries.list({
      search: searchQuery,
      current_stage: 'ready_for_packing',
      not_in_box: true,
      per_page: 10,
      ...(selectedFormId ? { dynamic_form_id: selectedFormId } : {}),
      ...(expectedClientId ? { client_id: expectedClientId } : {})
    }),
    enabled: searchQuery.length >= 3,
    // Drop the shared keepPreviousData so switching forms or clearing the
    // search doesn't bleed stale results from the previous query.
    placeholderData: undefined
  });

  const searchResults = (searchData?.data ?? []) as Registration[];

  const statusLabels: Record<BoxStatus, string> = useMemo(
    () => ({
      available: t('statusAvailable'),
      packing: t('statusPacking'),
      ready_for_shipping: t('statusReadyForShipping'),
      in_shipping: t('statusInShipping'),
      in_transit: t('statusInTransit'),
      received: t('statusReceived')
    }),
    [t]
  );

  async function handleSelectContainer(containerNumber: string) {
    try {
      await addRegMutation.mutateAsync({
        boxId,
        data: { container_number: containerNumber }
      });
      toggleAdd(false);
      setSearchQuery('');
    } catch (err) {
      if (isClientMismatchError(err)) {
        setMismatchContainer(containerNumber);
        toggleMismatch(true);
        toggleAdd(false);
        setSearchQuery('');
      }
      // other errors handled by global MutationCache
    }
  }

  async function handleConfirmClientMismatch() {
    if (!mismatchContainer) return;
    try {
      await addRegMutation.mutateAsync({
        boxId,
        data: { container_number: mismatchContainer, force: true }
      });
    } catch {
      // handled by global MutationCache
    }
    toggleMismatch(false);
  }

  function handleAddDialogOpenChange(open: boolean) {
    toggleAdd(open);
    if (!open) {
      setSearchQuery('');
    }
  }

  async function handleRemoveRegistration() {
    if (!removeTarget) return;
    try {
      await removeRegMutation.mutateAsync({
        boxId,
        registrationId: removeTarget.id
      });
    } catch {
      // handled by global MutationCache
    }
    closeRemove(false);
  }

  async function handleScannedContainer(containerNumber: string) {
    if (box?.status !== 'available' && box?.status !== 'packing') return;
    try {
      await addRegMutation.mutateAsync({
        boxId,
        data: { container_number: containerNumber }
      });
    } catch (err) {
      if (isClientMismatchError(err)) {
        setMismatchContainer(containerNumber);
        toggleMismatch(true);
      }
      // other errors handled by global MutationCache
    }
  }

  useHardwareScanner({
    onScan: handleScannedContainer,
    enabled: scannerActive
  });

  function handleCopySeal(seal: string) {
    navigator.clipboard.writeText(seal);
    toast.success(t('sealCopied'));
  }

  async function handleSaveSeals() {
    try {
      await updateBoxMutation.mutateAsync({
        id: boxId,
        data: {
          security_seal_1: seal1 || null,
          security_seal_2: seal2 || null
        }
      });
    } catch {
      // handled by global MutationCache
    }
  }

  const isOpenForFilling =
    box?.status === 'available' || box?.status === 'packing';
  const canEditSeals = isOpenForFilling && canEditBox;
  const sealsChanged =
    box &&
    (seal1 !== (box.security_seal_1 ?? '') ||
      seal2 !== (box.security_seal_2 ?? ''));

  const filteredRegistrations = useMemo(
    () =>
      selectedFormId && box
        ? (box.registrations ?? []).filter(
            (reg) => reg.dynamic_form_id === selectedFormId
          )
        : (box?.registrations ?? []),
    [box, selectedFormId]
  );

  // #remove/<registrationId> → resolve the hash param back to the matching
  // already-loaded registration (no fetch).
  const removeTarget = removeOpen
    ? ((box?.registrations ?? []).find((r) => String(r.id) === removeId) ??
      null)
    : null;

  const containerColumns = useMemo<ColumnDef<Registration>[]>(() => {
    const baseColumns: ColumnDef<Registration>[] = [
      {
        id: 'container_number',
        accessorKey: 'container_number',
        header: t('containerNumber'),
        cell: ({ row }) => (
          <span className='font-mono text-sm font-semibold text-blue-600'>
            {row.original.container_number}
          </span>
        ),
        meta: {
          label: t('containerNumber'),
          permission: 'box_containers.column.containerNumber'
        }
      },
      {
        id: 'containerType',
        accessorFn: (row) => row.container_type?.name ?? '',
        header: t('containerType'),
        cell: ({ row }) =>
          row.original.container_type ? (
            <Badge variant='outline'>{row.original.container_type.name}</Badge>
          ) : null,
        meta: {
          label: t('containerType'),
          permission: 'box_containers.column.containerType'
        }
      },
      {
        id: 'containerCurrentStage',
        accessorKey: 'current_stage',
        header: t('containerCurrentStage'),
        cell: ({ row }) => {
          const stage = row.original.current_stage;
          if (!stage) return null;
          const camel = stage
            .split('_')
            .map((p, i) =>
              i === 0 ? p : p.charAt(0).toUpperCase() + p.slice(1)
            )
            .join('');
          const pascal = camel.charAt(0).toUpperCase() + camel.slice(1);
          return (
            <StagePill stage={stage} label={tReg(`stage${pascal}` as never)} />
          );
        },
        meta: {
          label: t('containerCurrentStage'),
          permission: 'box_containers.column.containerCurrentStage'
        }
      },
      {
        id: 'subStatus',
        header: tReg('subStatus'),
        cell: ({ row }) => (
          <SubStatusCell
            stageStatus={row.original.stage_status}
            physicalStage={row.original.physical_stage}
          />
        ),
        meta: {
          label: tReg('subStatus'),
          permission: 'box_containers.column.subStatus'
        }
      },
      ...buildDynamicFormColumns(dynamicFormFields, { group: 'box_containers' })
    ];

    baseColumns.push({
      id: 'barcode',
      accessorKey: 'id',
      header: t('barcode'),
      cell: ({ row }) => (
        <span className='font-mono text-sm'>{row.original.id}</span>
      ),
      meta: {
        label: t('barcode'),
        permission: 'box_containers.column.barcode'
      }
    });

    // Manual removal is only allowed while the box is still being filled
    // (`available` or `packing`). Once it closes, registrations are locked —
    // the backend also rejects detach attempts on non-fillable boxes.
    if (isOpenForFilling && canEditBox) {
      baseColumns.push({
        id: 'actions',
        header: () => <span className='sr-only'>{t('remove')}</span>,
        cell: ({ row }) => (
          <Button
            variant='ghost'
            size='icon'
            className='h-8 w-8'
            onClick={() => openRemove(String(row.original.id))}
          >
            <Trash2 className='h-4 w-4 text-red-500' />
          </Button>
        ),
        enableHiding: false,
        enableSorting: false,
        size: 48
      });
    }

    return baseColumns;
  }, [t, tReg, isOpenForFilling, canEditBox, dynamicFormFields, openRemove]);

  const filteredColumns = useFilteredColumns(containerColumns);

  const containersTable = useTable({
    features: appTableFeatures,
    data: filteredRegistrations,
    columns: filteredColumns
  });

  if (isLoading) {
    return (
      <div className='space-y-6'>
        <Skeleton className='h-10 w-64' />
        <Skeleton className='h-24 w-full' />
        <div className='grid gap-6 lg:grid-cols-[1fr_2fr]'>
          <div className='space-y-6'>
            <Skeleton className='h-64 w-full' />
            <Skeleton className='h-48 w-full' />
          </div>
          <Skeleton className='h-96 w-full' />
        </div>
      </div>
    );
  }

  if (!box) {
    return (
      <div className='flex min-h-[400px] flex-col items-center justify-center'>
        <Package className='text-muted-foreground mb-4 h-12 w-12' />
        <p className='text-muted-foreground'>{t('boxNotFound')}</p>
        <Button
          variant='outline'
          className='mt-4'
          onClick={() => router.push('/dashboard/packing')}
        >
          {t('backToList')}
        </Button>
      </div>
    );
  }

  const maxCapacity = box.box_type?.max_capacity ?? 0;
  const isFull = box.registrations_count >= maxCapacity;
  const canAddContainers =
    (box.status === 'available' || box.status === 'packing') &&
    !isFull &&
    canEditBox;

  // Count registrations by container type
  const typeCounts: Record<string, number> = {};
  for (const reg of box.registrations ?? []) {
    const typeName = reg.container_type?.name ?? t('unknown');
    typeCounts[typeName] = (typeCounts[typeName] ?? 0) + 1;
  }

  return (
    <div className='space-y-6'>
      {/* Breadcrumb + back */}
      <div className='flex flex-wrap items-center gap-3'>
        <Button
          variant='outline'
          size='sm'
          onClick={() => router.push('/dashboard/packing')}
        >
          <ArrowLeft className='me-2 h-4 w-4 rtl:rotate-180' />
          {t('back')}
        </Button>
        <nav className='text-muted-foreground flex min-w-0 flex-wrap items-center gap-1 text-sm'>
          <Link href='/dashboard/packing' className='hover:text-foreground'>
            {t('pageTitle')}
          </Link>
          <span>/</span>
          <span className='text-foreground truncate font-medium'>
            {t('createNewBox')}
          </span>
        </nav>
      </div>

      {/* Page title */}
      <div>
        <h1 className='text-xl font-bold tracking-tight sm:text-2xl'>
          {t('createNewBox')}
        </h1>
        <p className='text-muted-foreground mt-1 text-sm sm:text-base'>
          {t('createBoxSubtitle')}
        </p>
      </div>

      {/* Box info bar */}
      <Card>
        <CardContent className='grid grid-cols-2 gap-4 py-4 sm:flex sm:flex-wrap sm:items-center sm:justify-between'>
          <div className='text-end'>
            <p className='text-muted-foreground text-xs'>{t('boxNumber')}</p>
            <p className='text-lg font-bold tracking-wide sm:text-xl'>
              {box.box_number}
            </p>
          </div>
          <div>
            <p className='text-muted-foreground text-xs'>{t('status')}</p>
            <Badge
              variant='secondary'
              className={cn('mt-1', STATUS_STYLES[box.status])}
            >
              {statusLabels[box.status]}
            </Badge>
          </div>
          <div className='col-span-2'>
            <p className='text-muted-foreground text-xs'>{t('creationDate')}</p>
            <p className='mt-1 text-sm font-medium'>
              {new Date(box.created_at).toLocaleDateString('ar-EG', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}{' '}
              {new Date(box.created_at).toLocaleTimeString('ar-EG', {
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Main content: 2 columns */}
      <div className='grid grid-cols-1 gap-6 lg:grid-cols-4'>
        {/* Left column */}
        <div className='col-span-1 space-y-6'>
          {/* Security Seals Card */}
          <Card>
            <CardHeader className='flex flex-row items-center gap-3 space-y-0 pb-2'>
              <ShieldCheck className='h-5 w-5 text-green-600' />
              <CardTitle className='text-base'>{t('securitySeals')}</CardTitle>
            </CardHeader>
            <CardContent className='space-y-4'>
              {/* Seal 1 */}
              <div>
                <p className='text-muted-foreground mb-1.5 text-xs'>
                  {t('securitySeal1')}
                </p>
                <div className='flex items-center gap-2'>
                  <Input
                    type='text'
                    inputMode='numeric'
                    value={seal1}
                    onChange={(e) =>
                      setSeal1(e.target.value.replace(/\D/g, ''))
                    }
                    placeholder={t('sealPlaceholder')}
                    className='font-mono'
                    disabled={!canEditSeals}
                  />
                  {seal1 && (
                    <Button
                      variant='ghost'
                      size='icon'
                      className='h-9 w-9 shrink-0'
                      onClick={() => handleCopySeal(seal1)}
                    >
                      <Copy className='h-4 w-4' />
                    </Button>
                  )}
                </div>
              </div>
              {/* Seal 2 */}
              <div>
                <p className='text-muted-foreground mb-1.5 text-xs'>
                  {t('securitySeal2')}
                </p>
                <div className='flex items-center gap-2'>
                  <Input
                    type='text'
                    inputMode='numeric'
                    value={seal2}
                    onChange={(e) =>
                      setSeal2(e.target.value.replace(/\D/g, ''))
                    }
                    placeholder={t('sealPlaceholder')}
                    className='font-mono'
                    disabled={!canEditSeals}
                  />
                  {seal2 && (
                    <Button
                      variant='ghost'
                      size='icon'
                      className='h-9 w-9 shrink-0'
                      onClick={() => handleCopySeal(seal2)}
                    >
                      <Copy className='h-4 w-4' />
                    </Button>
                  )}
                </div>
              </div>
              {!canEditSeals && (
                <p className='text-muted-foreground text-xs'>
                  {t('sealsLockedNotAvailable')}
                </p>
              )}
              {/* Save button */}
              <Button
                className='w-full'
                onClick={handleSaveSeals}
                disabled={
                  !canEditSeals || !sealsChanged || updateBoxMutation.isPending
                }
              >
                {updateBoxMutation.isPending && (
                  <Loader2 className='me-2 h-4 w-4 animate-spin' />
                )}
                {t('saveSeals')}
              </Button>
            </CardContent>
          </Card>

          {/* Box Summary Card */}
          <Card>
            <CardHeader>
              <CardTitle className='text-base'>{t('boxSummary')}</CardTitle>
            </CardHeader>
            <CardContent className='space-y-3'>
              <div className='flex items-center justify-between'>
                <span className='text-muted-foreground text-sm'>
                  {t('boxNumber')}
                </span>
                <span className='font-mono text-sm font-semibold'>
                  {box.box_number}
                </span>
              </div>
              <div className='flex items-center justify-between gap-2'>
                <span className='text-muted-foreground text-sm'>
                  {t('containerCount')}
                </span>
                <span className='text-sm font-semibold'>
                  {box.registrations_count} / {maxCapacity} {t('container')}
                </span>
              </div>
              {Object.entries(typeCounts).map(([type, count]) => (
                <div key={type} className='flex items-center justify-between'>
                  <span className='text-muted-foreground text-sm'>{type}</span>
                  <span className='text-sm font-semibold'>
                    {count} {t('container')}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Dynamic form responses */}
          {box.dynamic_form?.fields && box.dynamic_form.fields.length > 0 && (
            <DynamicFormResponsesDisplay
              formResponses={box.form_responses}
              fields={box.dynamic_form.fields}
            />
          )}

          {/* Action buttons */}
          <div className='space-y-2'>
            {box.status === 'packing' && canEditBox && (
              <>
                <Button
                  className='w-full'
                  size='lg'
                  disabled={
                    closeBoxMutation.isPending || box.registrations_count === 0
                  }
                  onClick={() => closeBoxMutation.mutate(boxId)}
                >
                  {closeBoxMutation.isPending ? (
                    <Loader2 className='me-2 h-4 w-4 animate-spin' />
                  ) : (
                    <PackageCheck className='me-2 h-4 w-4' />
                  )}
                  {t('closeBox')}
                </Button>
                {box.registrations_count === 0 && (
                  <p className='text-muted-foreground text-xs'>
                    {t('readyRequiresAtLeastOneContainer')}
                  </p>
                )}
              </>
            )}
            <Button
              className='w-full'
              size='lg'
              variant={isOpenForFilling ? 'outline' : 'default'}
              onClick={() => window.print()}
            >
              <QrCode className='me-2 h-4 w-4' />
              {t('printQR')}
            </Button>
            <Button
              variant='outline'
              className='w-full'
              size='lg'
              onClick={() => router.push('/dashboard/packing')}
            >
              {t('backToList')}
            </Button>
          </div>
        </div>

        {/* Right column — Assigned Containers */}
        <Card className='col-span-3'>
          <CardHeader className='flex flex-col gap-4 space-y-0 lg:flex-row lg:items-start lg:justify-between'>
            <div className='min-w-0 flex-1 space-y-2'>
              <CardTitle>{t('assignedContainers')}</CardTitle>
              <p className='text-muted-foreground text-sm'>
                {t('containersAddedOfMax', {
                  count: box.registrations_count ?? 0,
                  max: maxCapacity
                })}
              </p>
              <div className='flex items-center gap-2'>
                <FileText className='text-muted-foreground h-4 w-4 shrink-0' />
                <InfiniteCombobox
                  value={selectedFormId ? String(selectedFormId) : ''}
                  onChange={(v) => setSelectedFormIdStr(v || '')}
                  placeholder={t('registrationForm')}
                  infiniteConfig={formsSelect}
                  displayValue={selectedForm?.name}
                  className='h-8 w-full text-xs sm:w-auto sm:min-w-[200px]'
                />
              </div>
            </div>
            <div className='flex flex-wrap items-center gap-2'>
              {IS_QR_SCANNER_ENABLED && (
                <>
                  <Button
                    variant='outline'
                    disabled={!canAddContainers}
                    className='flex-1 sm:flex-none'
                    onClick={() => {
                      setScannerActive((prev) => {
                        if (!prev)
                          (document.activeElement as HTMLElement)?.blur();
                        return !prev;
                      });
                    }}
                  >
                    <span className='relative me-2 flex h-2 w-2 shrink-0'>
                      <span
                        className={cn(
                          'absolute inline-flex h-full w-full rounded-full opacity-75',
                          scannerActive && canAddContainers
                            ? 'animate-ping bg-green-400'
                            : 'bg-gray-400'
                        )}
                      />
                      <span
                        className={cn(
                          'relative inline-flex h-2 w-2 rounded-full',
                          scannerActive && canAddContainers
                            ? 'bg-green-500'
                            : 'bg-gray-400'
                        )}
                      />
                    </span>
                    <ScanLine className='me-2 h-4 w-4' />
                    {t('scanQR')}
                  </Button>
                  <Button
                    variant='outline'
                    size='icon'
                    disabled={!canAddContainers}
                    onClick={() => toggleScan(true)}
                    title={tc('scanCamera')}
                  >
                    <Camera className='h-4 w-4' />
                  </Button>
                </>
              )}
              <Button
                onClick={() => toggleAdd(true)}
                disabled={!canAddContainers}
                className='flex-1 sm:flex-none'
              >
                <Plus className='me-2 h-4 w-4' />
                {t('addContainer')}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {filteredRegistrations.length > 0 ? (
              <>
                <div className='mb-2 flex justify-end'>
                  <DataTableViewOptions
                    table={containersTable}
                    viewFilterPermission='box_containers.view_filter'
                  />
                </div>
                <div className='overflow-x-auto'>
                  <Table>
                    <TableHeader>
                      {containersTable.getHeaderGroups().map((headerGroup) => (
                        <TableRow key={headerGroup.id}>
                          {headerGroup.headers.map((header) => (
                            <TableHead key={header.id}>
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                            </TableHead>
                          ))}
                        </TableRow>
                      ))}
                    </TableHeader>
                    <TableBody>
                      {containersTable.getRowModel().rows.map((row) => (
                        <TableRow key={row.id}>
                          {row.getVisibleCells().map((cell) => (
                            <TableCell key={cell.id}>
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <p className='text-muted-foreground mt-4 text-center text-sm'>
                  {t('showingContainers', {
                    count: filteredRegistrations.length
                  })}
                </p>
              </>
            ) : (
              <div className='flex flex-col items-center justify-center py-12 text-center'>
                <Package className='text-muted-foreground mb-3 h-10 w-10' />
                <p className='text-muted-foreground'>
                  {t('noRegistrationsAssigned')}
                </p>
                {canAddContainers && (
                  <Button
                    variant='outline'
                    className='mt-4'
                    onClick={() => toggleAdd(true)}
                  >
                    <Plus className='me-2 h-4 w-4' />
                    {t('addContainer')}
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add container dialog — #add */}
      <Dialog open={addOpen} onOpenChange={handleAddDialogOpenChange}>
        <DialogContent className='max-w-md'>
          <DialogHeader>
            <DialogTitle>{t('addContainer')}</DialogTitle>
          </DialogHeader>
          <div className='space-y-4'>
            <div className='relative'>
              <Search className='text-muted-foreground absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2' />
              <Input
                placeholder={t('searchContainers')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className='ps-9'
                autoFocus
              />
            </div>

            {searchQuery.length < 3 && (
              <p className='text-muted-foreground text-center text-sm'>
                {t('typeToSearch')}
              </p>
            )}

            {searchQuery.length >= 3 && isSearching && (
              <div className='flex justify-center py-4'>
                <Loader2 className='text-muted-foreground h-5 w-5 animate-spin' />
              </div>
            )}

            {searchQuery.length >= 3 &&
              !isSearching &&
              searchResults.length === 0 && (
                <p className='text-muted-foreground text-center text-sm'>
                  {t('noMatch')}
                </p>
              )}

            {searchResults.length > 0 && (
              <div className='max-h-[300px] space-y-2 overflow-y-auto'>
                {searchResults.map((item) => (
                  <button
                    key={item.id}
                    type='button'
                    disabled={addRegMutation.isPending}
                    className='hover:bg-muted flex w-full items-center gap-3 rounded-lg border p-3 text-start transition-colors disabled:opacity-50'
                    onClick={() => handleSelectContainer(item.container_number)}
                  >
                    <div className='min-w-0 flex-1'>
                      <p className='font-mono font-semibold'>
                        {item.container_number}
                      </p>
                      <p className='text-muted-foreground text-sm'>
                        {item.container_type?.name ?? item.entity_name_ar}
                      </p>
                    </div>
                    {addRegMutation.isPending && (
                      <Loader2 className='text-muted-foreground h-4 w-4 shrink-0 animate-spin' />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Remove registration confirmation — #remove/<registrationId> */}
      <AlertDialog
        open={removeOpen && !!removeTarget}
        onOpenChange={closeRemove}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('removeRegistrationConfirmTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('removeRegistrationConfirmDescription', {
                number: removeTarget?.container_number ?? ''
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveRegistration}
              className={cn(
                'bg-destructive text-destructive-foreground hover:bg-destructive/90'
              )}
            >
              {removeRegMutation.isPending && (
                <Loader2 className='me-2 h-4 w-4 animate-spin' />
              )}
              {t('remove')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Client mismatch confirmation — #client-mismatch (hybrid: hash open,
          local payload) */}
      <AlertDialog
        open={mismatchOpen && !!mismatchContainer}
        onOpenChange={toggleMismatch}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('clientMismatchTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('clientMismatchDescription', {
                number: mismatchContainer ?? ''
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmClientMismatch}>
              {addRegMutation.isPending && (
                <Loader2 className='me-2 h-4 w-4 animate-spin' />
              )}
              {t('addAnyway')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <QrScanDialog
        open={scanOpen}
        onOpenChange={toggleScan}
        stage='packing'
        onScanValue={handleScannedContainer}
        translationNamespace='packing'
      />

      {/* Print-only QR zone */}
      <PrintPortal>
        <div className='hidden' data-print-zone>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '100vh',
              padding: '20mm',
              fontFamily: 'monospace'
            }}
          >
            <QRCode value={box.box_number} size={200} level='M' />
            <p
              style={{
                marginTop: '16px',
                fontSize: '20px',
                fontWeight: 'bold',
                letterSpacing: '2px'
              }}
            >
              {box.box_number}
            </p>
            {box.security_seal_1 && (
              <p style={{ marginTop: '8px', fontSize: '14px' }}>
                {t('securitySeal1')}: {box.security_seal_1}
              </p>
            )}
            {box.security_seal_2 && (
              <p style={{ marginTop: '4px', fontSize: '14px' }}>
                {t('securitySeal2')}: {box.security_seal_2}
              </p>
            )}
          </div>
        </div>
      </PrintPortal>
    </div>
  );
}
